import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Input, message, Modal, Progress, Select, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { api_active_task, api_bulkemail, api_stop_send } from '../api/bulkemail';

const { TextArea } = Input;
const { Option } = Select;

const BulkEmail: React.FC = () => {
    const [istest, setIstest] = useState(true);
    const [subject, setSubject] = useState("");
    const [content, setContent] = useState("");
    const [recipient, setRecipient] = useState<number | null>(null);
    const [attachment, setAttachment] = useState<File | null>(null); 
    const [fileList, setFileList] = useState<any[]>([]);
    const [taskId, setTaskId] = useState<string | null>(null);
    const [progress, setProgress] = useState<number>(0);
    const pollInterval = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        api_active_task().then((res) => {
            if (res.success && res.taskid) {
                setTaskId(res.taskid);
                pollProgress();
            }
        });
    }, []);

    const handleChange = (info: any) => {
        setFileList(info.fileList);
        if (info.fileList.length > 0) {
            setAttachment(info.fileList[0].originFileObj as File);
        } else {
            setAttachment(null);
        }
    };

    const handleSend = () => {
        if (taskId){
            api_stop_send().then((res) => {
                if (res.success) {
                    message.success('Bulk email sending stopped.');
                    if (pollInterval.current) {
                        clearInterval(pollInterval.current);
                        pollInterval.current = null;
                    }
                    resetForm();
                } else {
                    message.error('Failed to stop bulk email sending.');
                }
            });

            return;
        }

        if (!subject || !content || !recipient) {
            message.error('Please fill in all required fields.');
            return;
        }
        const formData = new FormData();
        formData.append("is_test", istest.toString());
        formData.append("subject", subject);
        formData.append("content", content);
        formData.append("recipient", recipient.toString());
        if (attachment) {
            formData.append("attachment", attachment);
        }

        if (istest) {
            api_bulkemail(formData)
                .then((response) => {
                    if (response.success) {
                        setTaskId(response.taskid);
                        setProgress(response.progress);
                        pollProgress();
                    } else {
                        message.error({ content: response.errorMsg, key: 'sendemail', duration: 2 });
                    }
                })
                .catch(() => {
                    message.error({ content: 'Exception occurred', key: 'sendemail', duration: 2 });
                });
        }
        else{
            Modal.confirm({
            title: 'Confirm Send',
            content: 'The current mode is not test mode. Do you really want to send emails to users?',
            okText: 'Yes',
            // cancelButtonProps: { style: { display: 'none' } },
            cancelText:'No',
            onOk: () => {
                api_bulkemail(formData)
                    .then((response) => {
                        if (response.success) {
                            setTaskId(response.taskid);
                            setProgress(response.progress);
                            pollProgress();
                        } else {
                            message.error({ content: response.errorMsg, key: 'sendemail', duration: 2 });
                        }
                    })
                    .catch(() => {
                        message.error({ content: 'Exception occurred', key: 'sendemail', duration: 2 });
                    });
                },
            });
        }

        //message.loading({ content: 'Sending emails...', key: 'sendemail', duration: 0 });
        
    };

    const resetForm = () => {
        setSubject("");
        setContent("");
        setRecipient(null);
        setAttachment(null);
        setFileList([]);
        setTaskId(null);
        setProgress(0);
        setIstest(true);
        pollInterval.current = null;
    };

    const pollProgress = () => {
        console.log("pollProgress start! pollInterval:", pollInterval);

        if (pollInterval.current) {
            console.log("Already running");
            return;
        }


        pollInterval.current = setInterval(async () => {
            api_active_task()
            .then((response) => {
                console.log(`progress: ${response.progress}`);
                if (response.success && response.taskid) {
                    setProgress(response.progress);
                    if (response.progress >= 100) {
                        clearInterval(pollInterval.current!);
                        message.success({ content: 'Bulk email sending completed!', key: 'sendemail', duration: 5 });
                        //alert('Bulk email sending completed!');
                        resetForm();
                    }
                } else {
                    clearInterval(pollInterval.current!);
                    if (response.taskid === null)
                    {
                        message.success({ content: 'No send mail', key: 'sendemail', duration: 2 });
                    }
                    else{
                        message.error({ content: response.errorMsg, key: 'sendemail', duration: 2 });
                    }
                    resetForm();
                }
            })
            .catch(() => {
                clearInterval(pollInterval.current!);
                message.error({ content: 'Exception occurred', key: 'sendemail', duration: 2 });
            });
        }, 2000);
    };

    return (
        <div className='text-style tab-container'>
            <div className="p-6 max-w-2xl mx-auto m-12">
                <Card className="shadow-lg rounded-2xl bg-gray-100">
                    <h2 className="text-xl font-bold mb-4">Bulk Email Send</h2>
                    
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 m-5">
                            <Input
                                type='checkbox'
                                checked={istest}
                                className='w-4 h-4'
                                onChange={(e) => setIstest(e.target.checked)}
                            />
                            <label className="font-medium w-24">Test Mode</label>
                        </div>

                        {/* Subject */}
                        <div className="flex items-center gap-2">
                            <label className="font-medium w-24">Subject:</label>
                            <Input
                                placeholder="Enter subject"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                            />
                        </div>

                        {/* Content */}
                        <div className="flex items-center gap-2">
                            <label className="font-medium w-24">Content:</label>
                            <TextArea
                                placeholder="Enter content"
                                rows={6}
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                className="mt-1"
                            />
                        </div>

                        {/* Recipient selection */}
                        <div className="flex items-center gap-2">
                            <label className="font-medium w-24">To:</label>
                            <Select
                                value={recipient || undefined}
                                onChange={(value) => setRecipient(value)}
                                className="w-full"
                                placeholder="Please select a recipient"
                            >
                                <Option value={1}>All</Option>
                                <Option value={2}>Brokers Only</Option>
                                <Option value={3}>Users Only</Option>
                                <Option value={4}>Arizona Brokers Only</Option>
                                <Option value={5}>Arizona Users Only</Option>
                            </Select>
                        </div>

                        {/* Attachment */}
                        <div>
                            <label className="font-medium mr-2">Attachment:</label>
                            <Upload
                                beforeUpload={() => false}
                                onChange={handleChange}
                                maxCount={1}
                                fileList={fileList}
                            >
                                <Button icon={<UploadOutlined />}>Upload</Button>
                            </Upload>
                        </div>

                        {/* Progress Bar */}
                        {taskId && (
                            <div className="flex items-center gap-2 mt-4">
                                <label className="font-medium  w-40">Sending Emails...</label>
                                <Progress percent={progress} status={progress < 100 ? "active" : "success"} />
                            </div>
                        )}
                        {/* Send Button */}
                        <div className='grid justify-end'>
                            <Button type="primary" onClick={handleSend} className='w-32'>
                                {taskId ? 'Stop' : 'Send'}
                            </Button>
                        </div>

                    </div>
                </Card>
            </div>
        </div>
    );
};

export default BulkEmail;